import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import calc, { LOADING } from '../../../utils/calc'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

interface IMetricNode {
  startDate: string
  endDate: string
  metricData: Record<
    'total_sales',
    {
      name: string
      unit: 'CENT'
      value: number
    }
  >
}

type IDataType = {
  trendLocationGroupMetricValues: {
    nodes: IMetricNode[]
  }
}

const createCurrentQuery = (yearIndex: number) => gql`
  query listLocationMetricValuesV2Year${yearIndex}(
    $iStartDate: Date!
    $iEndDate: Date!
    $iLocationGroupIds: [Int!]!
    $iMetricCodes: [String!]!
  ) {
    trendLocationGroupMetricValues: listLocationMetricValuesV2(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iParams: {
        location_group_ids: $iLocationGroupIds
        group_by: ["LOCATION_GROUP", "BUSINESS_MONTH"]
        metrics: $iMetricCodes
        use_location_history: true
      }
    ) {
      nodes {
        startDate
        endDate
        metricData
      }
    }
  }
`

const createYoyQuery = (yearIndex: number) => gql`
  query listLocationMetricValuesV2YoyYear${yearIndex}(
    $iStartDate: Date!
    $iEndDate: Date!
    $iLocationGroupIds: [Int!]!
    $iMetricCodes: [String!]!
  ) {
    trendLocationGroupMetricValues: listLocationMetricValuesV2(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iParams: {
        location_group_ids: $iLocationGroupIds
        group_by: ["LOCATION_GROUP", "BUSINESS_MONTH"]
        metrics: $iMetricCodes
        use_location_history: true
        use_yoy: true
      }
    ) {
      nodes {
        startDate
        endDate
        metricData
      }
    }
  }
`

const currentQueries = Array.from({ length: 4 }, (_, i) =>
  createCurrentQuery(i),
)
const yoyQueries = Array.from({ length: 4 }, (_, i) => createYoyQuery(i))

export const bbbAllPeriodMetricConfigs = {
  year: 'string',
  '<%- JSON(date?.getCalendar("period", "year").ids.map(id => ({ key: "compSalesPercent" + id, title: id }))) %>':
    'percent',
} as const

const HARDCODE_DATA = {
  '2022P1': 5.66,
  '2022P2': 15.55,
  '2022P3': 4.62,
  '2022P4': -4.38,
  '2022P5': 2.42,
  '2022P6': 2.52,
  '2022P7': 1.97,
  '2022P8': 11.64,
  '2022P9': 8.27,
  '2022P10': 7.12,
  '2022P11': 6.48,
  '2022P12': 3.45,
} as Record<`${number}P${number}`, number>
const HARDCODE_YEAR = 2022
const MAX_YEARS_TO_SHOW = 5
const NOW = moment.utc()

const shouldSkipYear = (
  periods: { year?: number }[],
  skip: boolean,
): boolean => {
  if (skip || periods.length === 0) return true
  const year = periods[0]?.year
  return !year || year <= HARDCODE_YEAR
}

const useBbbAllPeriodMetric = () => {
  const { variables } = useVariables()
  const periodCalendar = useMemo(
    () => variables.date?.getCalendar('period')?.calendar || [[]],
    [variables.date],
  )

  const currentYear = periodCalendar[0]?.[0]?.year || new Date().getFullYear()
  const oldestYearToShow = Math.max(
    currentYear - MAX_YEARS_TO_SHOW + 1,
    HARDCODE_YEAR,
  )

  const yearPeriods = Array.from(
    { length: 4 },
    (_, i) => periodCalendar[i] || [],
  )

  const baseVariables = {
    iLocationGroupIds: [variables.bbbConstants?.COMP_GROUP_ID],
    iMetricCodes: ['total_sales'],
  }
  const skip = !variables.date || !variables.bbbConstants

  const skipYear0 = shouldSkipYear(yearPeriods[0], skip)
  const { data: currentData0, loading: currentLoading0 } = useQuery<IDataType>(
    currentQueries[0],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[0][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[0].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear0,
    },
  )
  const { data: yoyData0, loading: yoyLoading0 } = useQuery<IDataType>(
    yoyQueries[0],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[0][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[0].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear0,
    },
  )

  const skipYear1 =
    shouldSkipYear(yearPeriods[1], skip) || !currentData0 || !yoyData0
  const { data: currentData1, loading: currentLoading1 } = useQuery<IDataType>(
    currentQueries[1],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[1][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[1].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear1,
    },
  )
  const { data: yoyData1, loading: yoyLoading1 } = useQuery<IDataType>(
    yoyQueries[1],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[1][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[1].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear1,
    },
  )

  const skipYear2 =
    shouldSkipYear(yearPeriods[2], skip) || !currentData1 || !yoyData1
  const { data: currentData2, loading: currentLoading2 } = useQuery<IDataType>(
    currentQueries[2],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[2][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[2].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear2,
    },
  )
  const { data: yoyData2, loading: yoyLoading2 } = useQuery<IDataType>(
    yoyQueries[2],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[2][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[2].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear2,
    },
  )

  const skipYear3 =
    shouldSkipYear(yearPeriods[3], skip) || !currentData2 || !yoyData2
  const { data: currentData3, loading: currentLoading3 } = useQuery<IDataType>(
    currentQueries[3],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[3][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[3].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear3,
    },
  )
  const { data: yoyData3, loading: yoyLoading3 } = useQuery<IDataType>(
    yoyQueries[3],
    {
      variables: {
        ...baseVariables,
        iStartDate: yearPeriods[3][0]?.dateRange.startDateStr,
        iEndDate: yearPeriods[3].slice(-1)[0]?.dateRange.endDateStr,
      },
      skip: skipYear3,
    },
  )

  const loading =
    currentLoading0 ||
    yoyLoading0 ||
    currentLoading1 ||
    yoyLoading1 ||
    currentLoading2 ||
    yoyLoading2 ||
    currentLoading3 ||
    yoyLoading3

  return {
    data: useMemo((): IApiDataType => {
      const filteredCalendar = periodCalendar.filter(
        (periods) => periods[0]?.year >= oldestYearToShow,
      )

      if (filteredCalendar.length === 0) return null

      const yearDataMap: Record<
        number,
        { current: IDataType | undefined; yoy: IDataType | undefined }
      > = {}
      const yearPeriodsData = [
        { current: currentData0, yoy: yoyData0, periods: yearPeriods[0] },
        { current: currentData1, yoy: yoyData1, periods: yearPeriods[1] },
        { current: currentData2, yoy: yoyData2, periods: yearPeriods[2] },
        { current: currentData3, yoy: yoyData3, periods: yearPeriods[3] },
      ]
      yearPeriodsData.forEach(({ current, yoy, periods }) => {
        const year = periods[0]?.year
        if (year && year > HARDCODE_YEAR) {
          yearDataMap[year] = { current, yoy }
        }
      })

      return filteredCalendar.map((periods) => {
        const year = periods[0]?.year
        const yearData = yearDataMap[year]
        const isHardcodedYear = year === HARDCODE_YEAR
        const hasYearData = yearData?.current && yearData?.yoy

        const getCurrentValue = (periodData: typeof periods[number]) => {
          if (!hasYearData) return LOADING
          const node =
            yearData.current!.trendLocationGroupMetricValues.nodes.find(
              (n) =>
                n.startDate === periodData.dateRange.startDateStr &&
                n.endDate === periodData.dateRange.endDateStr,
            )
          return node?.metricData.total_sales.value ?? null
        }

        const getYoyValue = (periodData: typeof periods[number]) => {
          if (!hasYearData) return LOADING
          const node = yearData.yoy!.trendLocationGroupMetricValues.nodes.find(
            (n) =>
              n.startDate === periodData.dateRange.startDateStr &&
              n.endDate === periodData.dateRange.endDateStr,
          )
          return node?.metricData.total_sales.value ?? null
        }

        const getValue = (periodData: typeof periods[number]) => {
          if (isHardcodedYear && oldestYearToShow <= HARDCODE_YEAR) {
            return (
              HARDCODE_DATA[
                `${periodData.year}${periodData.id}` as `${number}P${number}`
              ] ?? null
            )
          }

          const currentValue = getCurrentValue(periodData)
          const yoyValue = getYoyValue(periodData)

          return calc(
            calc(currentValue, '-', yoyValue),
            'percentageOf',
            yoyValue,
          )
        }

        const periodColumns: Record<string, number | null | typeof LOADING> = {}
        for (const period of periods) {
          if (period.dateRange.endDate.isAfter(NOW)) continue

          const periodKey = `compSalesPercent${period.id}`
          periodColumns[periodKey] = getValue(period)
        }

        return {
          ...periodColumns,
          id: year.toString(),
          parentId: 'root',
          year: year.toString(),
        }
      })
    }, [
      currentData0,
      currentData1,
      currentData2,
      currentData3,
      yoyData0,
      yoyData1,
      yoyData2,
      yoyData3,
      periodCalendar,
      oldestYearToShow,
      yearPeriods,
    ]),
    loading,
  }
}

export default useBbbAllPeriodMetric
